import { api_configuration } from "common";
import * as Stacktrace from "stacktrace-js";
import {
	client_state,
	engine_version,
	generateClientStateReport,
	listLastInstructions,
	settings,
} from "./state/ClientState";
import { DataStoreProtocolError, DatastoreProxy } from "./interface/DatastoreProxy";
import * as messages from "./front/messages";
import api_cs = api_configuration.types.client_state.alan_api;
import ClientStateObjectProxy from "./state/ClientStateObjectProxy";

async function mapStack(error: Error): Promise<string> {
	try {
		const mapped = await Stacktrace.fromError(error);
		return "\t" + mapped.map((f) => f.toString()).join("\n\t");
	} catch (e) {
		return error.stack || "";
	}
}

function ignoreError(error: Error) {
	if (error instanceof DataStoreProtocolError) {
		return true;
	}
	if (ignoreMessage(error.message)) {
		return true;
	}
	return false;
}

function ignoreMessage(message: string) {
	if (message.toLowerCase().indexOf("script error") >= 0) {
		return true;
	}
	if (message.toLowerCase().indexOf("request error") >= 0) {
		return true;
	}
	if (message.toLowerCase().indexOf("failed to fetch") >= 0) {
		return true;
	}
	if (message.toLocaleLowerCase() === "load failed") {
		// https://stackoverflow.com/questions/71280168/javascript-typeerror-load-failed-error-when-calling-fetch-on-ios
		return true;
	}
	if (message.toLowerCase().indexOf("possible side-effect in debug-evaluate") >= 0) {
		return true;
	}
	//The loop limit exceeded message can be ignored according to the internet
	//https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
	if (message === "ResizeObserver loop limit exceeded") {
		return true;
	}
	//https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
	if (message === "ResizeObserver loop completed with undelivered notifications.") {
		return true;
	}
	return false;
}

export function setupErrorHandlers(on_error: (title: string, message: string) => void) {
	const old_console_error = console.error;
	(<any>Error).stackTraceLimit = Infinity;
	function formatStackLine(s: string) {
		return "\tat " + s;
	}

	const stacktracelimit = 15;
	(<any>Error).prepareStackTrace = function (_: any, structuredStackTrace: string[]) {
		const stacksize = structuredStackTrace.length;
		let formattedStack = structuredStackTrace.slice(0, stacktracelimit).map(formatStackLine);
		if (stacksize > stacktracelimit) {
			const taillimit = Math.min(stacktracelimit, stacksize - stacktracelimit);
			formattedStack = formattedStack
				.concat("\t... (" + (stacksize - stacktracelimit - taillimit) + ")")
				.concat(structuredStackTrace.slice(-taillimit).map(formatStackLine));
		}
		return formattedStack.join("\n");
	};
	function logErrorToServer(error: string) {
		if (!global.errorCaughtAndSent) {
			sendMessageToServer(error);
			global.errorCaughtAndSent = true;
			if (settings) {
				settings.properties.settings.properties.reload_on_unexpected_error.switch({
					enabled: function () {
						window.location.reload();
					},
					disabled: function () {
						if (client_state) {
							messages.addMessage("error", "Unexpected error", {
								body: "The error has been reported. Refresh your browser before continuing.",
							});
						} else {
							on_error("Unexpected error", "The error has been reported. Refresh your browser before continuing.");
						}
					},
				});
			} else {
				if (client_state) {
					messages.addMessage("error", "Unexpected error", {
						body: "The error has been reported. Refresh your browser before continuing.",
					});
				} else {
					on_error("Unexpected error", "The error has been reported. Refresh your browser before continuing.");
				}
			}
		}
	}
	function tryGetApplicationStateSnapShot() {
		try {
			return JSON.stringify(
				createApplicationStateSnapShot({
					include_browser_details: true,
					include_client_state: include_client_state,
					include_url: global.logClientStateOnError,
				}),
				createClientStateStringyfier(),
				2
			);
		} catch (e) {
			return "Application state can not be determined.";
		}
	}
	function createMessageAndSendToServer(message_to_server: string) {
		logErrorToServer(message_to_server + "\n" + tryGetApplicationStateSnapShot());
	}
	console.error = async function (...args) {
		old_console_error.apply(console, args);
		const message = await Promise.all(
			Array.from(args).map(async (arg) => {
				if (arg instanceof Error) {
					return arg.name + " " + arg.message + "\n" + (await mapStack(arg));
				} else {
					return arg;
				}
			})
		);
		createMessageAndSendToServer(message.join(""));
	};
	window.addEventListener("error", async function (evt) {
		if (evt.error instanceof Error && ignoreError(evt.error)) {
			return;
		}
		if (ignoreMessage(evt.message)) {
			return;
		}
		let errorStack = "";
		if (evt.error instanceof Error) {
			errorStack = await mapStack(evt.error);
			errorStack = "\n" + errorStack + "\n";
		}
		createMessageAndSendToServer("'" + evt.message + "' from " + evt.filename + ":" + evt.lineno + errorStack);
	});
	window.addEventListener("unhandledrejection", async function (rejection) {
		if (rejection.reason instanceof Error && ignoreError(rejection.reason)) {
			return;
		}
		let message = "";
		if (rejection.reason instanceof Error) {
			message = rejection.reason.message;
		} else {
			try {
				message = JSON.stringify(rejection.reason);
			} catch (e) {
				message = rejection.reason?.toString();
			}
		}
		if (ignoreMessage(message)) {
			return;
		}
		let errorStack = "";
		if (rejection.reason?.stack !== undefined) {
			if (rejection.reason instanceof Error) {
				errorStack = await mapStack(rejection.reason);
				errorStack = "\n" + errorStack + "\n";
			} else {
				errorStack = "\n" + rejection.reason.stack + "\n";
			}
		}
		createMessageAndSendToServer("'" + message + errorStack);
	});
}

function sendMessageToServer(message: string) {
	const http_request = new XMLHttpRequest();
	http_request.open("POST", "__log_error");
	http_request.send(message);
}

function createClientStateStringyfier() {
	let client_state_seen = false;
	return (key: string, value: any) => {
		if (client_state_seen) {
			return value;
		} else if (key === "client state") {
			client_state_seen = true;
			return JSON.stringify(value, ignoreAlanObjects);
		} else {
			return value;
		}
	};
}

function ignoreAlanObjects(key: string, value: any) {
	if (key === "_$") return undefined;
	if (value instanceof api_cs.AlanObject) return "…";
	if (value instanceof ClientStateObjectProxy) return "…";
	return value;
}

let include_client_state = false;
export function includeClientStateInErrorLog() {
	include_client_state = true;
}

export function sendFeedback(message: string): void {
	const snapshot = createApplicationStateSnapShot({
		include_client_state: true,
		include_url: true,
		include_browser_details: true,
	});
	sendMessageToServer(message + "\n" + JSON.stringify(snapshot, createClientStateStringyfier(), 2));
}

export function createApplicationStateSnapShot(
	options = {
		include_client_state: false,
		include_url: false,
		include_browser_details: true,
	}
) {
	function tryGetWebSocketConnectionState() {
		try {
			return DatastoreProxy.instance.connection.isWebSocketOpen() ? "Websocket connected" : "Websocket disconnected";
		} catch (e) {
			return "could not be determined";
		}
	}

	function tryGetAuthenticationStatus() {
		if (!global.logClientStateOnError) {
			return "not available";
		}
		try {
			const session = client_state.properties.session;
			switch (session.properties.status.state.name) {
				case "not authenticated": {
					return "not authenticated";
				}
				case "authenticated": {
					const $ = session.properties.status.state.node;
					return [
						"authenticated",
						{
							user: $.properties.anonymous_session.switch({
								yes: "<anonymous>",
								no: ($) => $.properties.username,
							}),
						},
					];
				}
			}
		} catch (e) {
			return "could not be determined";
		}
	}

	function tryserializeIdPath(id_path: api_configuration.types.client_state.alan_api.Cclient_id_path): string {
		return id_path.properties.has_steps.switch({
			yes: function ($) {
				return (
					$.properties.type.switch<string>({
						operation: ($) => "." + $.properties.operation.entry,
						"collection entry": function ($) {
							let key_value = $.properties.entry.entry;
							try {
								const key = $.properties.entry.ref.properties.key.ref;
								switch (key.properties.is_set.state.name) {
									case "yes": {
										key_value = key.properties.is_set.state.node.properties.text + ":" + key_value;
										break;
									}
									case "no": {
										break;
									}
								}
							} catch (e) {
								// ignore
							}
							return "." + $.properties.collection.entry + "[" + key_value + "]";
						},
						group: function ($) {
							return "." + $.properties.group.entry;
						},
						state: function ($) {
							return "." + $.properties.state_group.entry + "?" + $.properties.state.entry;
						},
					}) + tryserializeIdPath($.properties.tail)
				);
			},
			no: "",
		});
	}

	function tryGetOpenViews(): any {
		if (!global.logClientStateOnError) {
			return "not available";
		}
		try {
			const open_views: Record<string, unknown> = {};
			client_state.properties.windows.forEach(function (window) {
				window.properties.open_views.forEach(function (open_view) {
					open_views[open_view.key] = {
						context: tryserializeIdPath(open_view.properties.view_context),
						"view definition": open_view.properties.view_definition.ref.path,
						active: open_view.properties.is_active_view.switch({
							yes: "YES!",
							no: "no",
						}),
					};
				});
			});
			return open_views;
		} catch (e) {
			return "could not be determined";
		}
	}

	function tryListLastInstructions() {
		if (!global.logClientStateOnError) {
			return "not available";
		}
		return listLastInstructions();
	}

	const log = {
		"engine version": engine_version,
		"websocket status": tryGetWebSocketConnectionState(),
		"authentication status": tryGetAuthenticationStatus(),
		"open views": tryGetOpenViews(),
		"last instructions": tryListLastInstructions(),
	};
	if (options.include_url) {
		try {
			(log as any)["url"] = window.location.toString();
		} catch (e) {
			// ignore error
		}
	}
	if (options.include_browser_details) {
		const any_performance = (window as any).performance.memory;
		if (any_performance) {
			try {
				const memory = {
					jsHeapSizeLimit: any_performance.jsHeapSizeLimit,
					totalJSHeapSize: any_performance.totalJSHeapSize,
					usedJSHeapSize: any_performance.usedJSHeapSize,
				};
				(log as any)["memory"] = memory;
			} catch (e) {
				// too bad
			}
		}
		try {
			const browser_details = {
				userAgent: window.navigator.userAgent,
				language: window.navigator.language,
			};
			(log as any)["browser"] = browser_details;
		} catch (e) {
			console.warn("Could not log browser details");
		}
	}
	if (options.include_client_state) {
		try {
			(log as any)["client state"] = generateClientStateReport();
		} catch (e) {
			(log as any)["client state"] = "Client state could not be created";
			console.warn("Client state could not be created");
		}
	}
	return log;
}
